/**
 * Status model: wet / burning / chilled / frozen / shocked / knockdown /
 * hasted / slowed and the damage multipliers they cause (DESIGN 2.7).
 */

import type { Element } from './elements.js';
import { BURN, DAMAGE_MUL, STATUS_SECONDS } from './constants.js';

/** `blinded` (Ash): an enemy loses its target and wanders; it does nothing to a wizard. */
export type StatusId = 'wet'|'burning'|'chilled'|'frozen'|'shocked'|'knockdown'|'hasted'|'slowed'|'mired'|'blinded';

/** `t` = seconds left; `stacks` only matters for burning (1..6). */
export interface StatusState { id: StatusId; t: number /* seconds left */; stacks: number }

/**
 * Anything that can take damage and carry statuses. `armor` is the orc-style
 * armor pool (fire x0, physical x0.5, lightning x1.5 while > 0); `immune` is
 * the union of elements zeroed by resistance auras the entity stands in and
 * its own traits (jotunn: lightning).
 */
/** `noFreeze` (Mother Mire): cold chills but never freezes solid; absent means it can. */
export interface Vulnerable { statuses: StatusState[]; armor: number; undead: boolean; noWet: boolean; immune: Element[]; noFreeze?: boolean }

export type DamageType = 'physical'|'arcane'|'life'|'fire'|'cold'|'lightning'|'steam'|'water';

export const STATUS_IDS: readonly StatusId[] = ['wet', 'burning', 'chilled', 'frozen', 'shocked', 'knockdown', 'hasted', 'slowed', 'mired', 'blinded'];
export const DAMAGE_TYPES: readonly DamageType[] = ['physical', 'arcane', 'life', 'fire', 'cold', 'lightning', 'steam', 'water'];

/** Duration in seconds when (re)applied (DESIGN 2.7 table). */
export const STATUS_DURATION: Readonly<Record<StatusId, number>> = { ...STATUS_SECONDS };

/**
 * Elements whose resistance aura (or innate immunity) blocks a status
 * (DESIGN 2.5 Aura: "immune to those elements' damage AND statuses").
 */
const STATUS_ELEMENTS: Readonly<Record<StatusId, readonly Element[]>> = {
  wet: ['water', 'steam'],
  burning: ['fire'],
  chilled: ['cold', 'ice'],
  frozen: ['cold', 'ice'],
  shocked: ['lightning'],
  knockdown: ['earth'],
  hasted: [],
  slowed: [],
  mired: ['mud', 'earth'],
  blinded: ['ash'],
};

/** The element a damage type's immunity key is (physical has none). */
const DAMAGE_ELEMENT: Readonly<Record<DamageType, Element | null>> = {
  physical: null, arcane: 'arcane', life: 'life', fire: 'fire', cold: 'cold', lightning: 'lightning', steam: 'steam', water: 'water',
};

function copy(list: readonly StatusState[]): StatusState[] {
  return list.map((s) => ({ ...s }));
}

function drop(list: StatusState[], ...ids: StatusId[]): void {
  for (let i = list.length - 1; i >= 0; i--) {
    const s = list[i];
    if (s !== undefined && ids.includes(s.id)) list.splice(i, 1);
  }
}

function find(list: readonly StatusState[], id: StatusId): StatusState | undefined {
  return list.find((s) => s.id === id);
}

/** Refresh `id` to its full duration if present, else append it (DESIGN 2.7 "refresh on re-apply"). */
function refresh(list: StatusState[], id: StatusId, stacks = 1): void {
  const existing = find(list, id);
  if (existing) { existing.t = STATUS_DURATION[id]; existing.stacks = stacks; return; }
  list.push({ id, t: STATUS_DURATION[id], stacks });
}

/**
 * Apply a status and its interactions (DESIGN 2.7 "Applied by / Removed by"):
 * wet removes burning; burning removes wet/chilled/frozen; chilled on a wet
 * target becomes frozen (replacing wet); noWet targets ignore wet; burning
 * stacks to 6 and refreshes. Returns the new status list in application
 * order (does not mutate `v.statuses`). `now` is the simulation time in
 * seconds; v1 durations are relative so it is accepted for the fixed
 * signature and not needed.
 *
 * Choices where 2.7 is silent, made for legibility (at most one cold state
 * and one water state at a time): wet on a frozen target is ignored (ice is
 * already there; water does not thaw); chilled on a frozen target is ignored
 * (frozen outranks chilled); frozen replaces chilled as well as wet, so a
 * thawed target is simply dry.
 */
export function applyStatus(v: Vulnerable, id: StatusId, now: number, stacks?: number): StatusState[] {
  void now;
  const list = copy(v.statuses);
  if (STATUS_ELEMENTS[id].some((el) => v.immune.includes(el))) return list;
  switch (id) {
    case 'wet':
      if (v.noWet || find(list, 'frozen')) return list;
      // Water on a burning target puts the fire out and that is all: it does not also soak them.
      if (find(list, 'burning')) { drop(list, 'burning'); return list; }
      refresh(list, 'wet');
      return list;
    case 'burning': {
      // Fire on a wet (or muddy) target dries it and that is all: it does not also set it alight.
      if (find(list, 'wet')) { drop(list, 'wet', 'mired'); return list; }
      drop(list, 'chilled', 'frozen', 'mired');
      const existing = find(list, 'burning');
      // `stacks` given: set to at least that many (a channel tick); otherwise +1 per application.
      const next = stacks !== undefined ? Math.max(existing?.stacks ?? 0, stacks) : (existing ? existing.stacks + 1 : 1);
      refresh(list, 'burning', Math.max(1, Math.min(BURN.maxStacks, next)));
      return list;
    }
    case 'chilled':
      if (find(list, 'frozen')) return list;
      drop(list, 'burning');
      if (find(list, 'wet') && !v.noFreeze) {
        // DESIGN 2.7: cold on a wet target freezes; frozen replaces wet.
        drop(list, 'wet', 'chilled');
        refresh(list, 'frozen');
        return list;
      }
      refresh(list, 'chilled');
      return list;
    case 'frozen':
      if (v.noFreeze) {
        drop(list, 'burning');
        refresh(list, 'chilled');
        return list;
      }
      drop(list, 'wet', 'burning', 'chilled');
      refresh(list, 'frozen');
      return list;
    default:
      refresh(list, id);
      return list;
  }
}

/** Returns the status list without `id` (no-op when absent). Does not mutate. */
export function removeStatus(v: Vulnerable, id: StatusId): StatusState[] {
  return copy(v.statuses.filter((s) => s.id !== id));
}

export function hasStatus(v: Vulnerable, id: StatusId): boolean {
  return v.statuses.some((s) => s.id === id);
}

/**
 * Product of all applicable multipliers (DESIGN 2.7): lightning x2 if wet,
 * x1.5 if armor > 0; x3 anything if frozen; fire x0 and physical x0.5 while
 * armored; immune elements x0. Sign inversion for undead is handled by
 * `resolveDamageSign`. Frozen triples damage, not healing: a type that heals
 * this target (`resolveDamageSign` -1) skips the x3.
 */
export function damageMultiplier(v: Vulnerable, type: DamageType): number {
  const el = DAMAGE_ELEMENT[type];
  if (el !== null && v.immune.includes(el)) return 0;
  let mul = 1;
  const armored = v.armor > 0;
  if (type === 'lightning') {
    if (hasStatus(v, 'wet')) mul *= DAMAGE_MUL.wetLightning;
    if (armored) mul *= DAMAGE_MUL.armoredLightning;
  }
  if (type === 'fire' && armored) mul *= DAMAGE_MUL.armoredFire;
  if (type === 'physical' && armored) mul *= DAMAGE_MUL.armoredPhysical;
  if (hasStatus(v, 'frozen') && resolveDamageSign(v, type) === 1) mul *= DAMAGE_MUL.frozen;
  return mul;
}

/** +1 normally; undead flip life (becomes damage) and arcane (becomes healing). */
export function resolveDamageSign(v: Vulnerable, type: DamageType): 1 | -1 {
  if (type === 'life') return v.undead ? 1 : -1;
  if (type === 'arcane') return v.undead ? -1 : 1;
  return 1;
}

/**
 * Advance timers by dt seconds, in place: every entry's `t` is decremented
 * and expired entries are spliced out of `v.statuses` (the array keeps its
 * identity; the sim loop owns it). Returns the burning damage accrued over
 * dt (20/s per stack, exact for any dt so the server may bank it and pop
 * the number once per second at BURN_TICK_EVERY) and the ids that expired.
 * Wet and burning never coexist (applyStatus removes one with the other),
 * so the "-1 burn stack per second while wet" rule has nothing to drain.
 */
export function tickStatuses(v: Vulnerable, dt: number): { dot: number; expired: StatusId[] } {
  let dot = 0;
  const expired: StatusId[] = [];
  for (let i = v.statuses.length - 1; i >= 0; i--) {
    const s = v.statuses[i];
    if (s === undefined) continue;
    if (s.id === 'burning') dot += BURN.dpsPerStack * s.stacks * Math.max(0, Math.min(dt, s.t));
    s.t -= dt;
    if (s.t <= 0) {
      expired.push(s.id);
      v.statuses.splice(i, 1);
    }
  }
  expired.reverse();
  return { dot, expired };
}

/**
 * The status a damage type carries by itself: water/steam -> wet,
 * fire -> burning, cold -> chilled, lightning -> shocked; physical, arcane and
 * life -> null.
 */
export function statusOnDamageType(type: DamageType): StatusId | null {
  switch (type) {
    case 'water':
    case 'steam': return 'wet';
    case 'fire': return 'burning';
    case 'cold': return 'chilled';
    case 'lightning': return 'shocked';
    default: return null;
  }
}
